import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  User as UserIcon, Mail, Phone, MapPin, Globe, Loader2,
  Ticket as TicketIcon, FileText, Receipt, Calendar, MessageCircle, Briefcase,
  Plus, Send, FileEdit, CalendarPlus, Key as KeyIcon,
} from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  open: 'border-amber-500/40 text-amber-400',
  in_progress: 'border-blue-500/40 text-blue-400',
  waiting_on_client: 'border-purple-500/40 text-purple-400',
  closed: 'border-muted text-muted-foreground',
  draft: 'border-muted text-muted-foreground',
  sent: 'border-blue-500/40 text-blue-400',
  accepted: 'border-primary/50 text-primary',
  rejected: 'border-red-500/40 text-red-400',
  paid: 'border-primary/50 text-primary',
  pending: 'border-amber-500/40 text-amber-400',
  overdue: 'border-red-500/40 text-red-400',
  active: 'border-primary/50 text-primary',
  cancelled: 'border-muted text-muted-foreground',
};

const fmt = (iso, pattern = 'MMM dd, yyyy') => {
  if (!iso) return '—';
  try { return format(new Date(iso), pattern); } catch { return iso; }
};

const SectionHeader = ({ icon: Icon, label, count }) => (
  <div className="flex items-center gap-2 mb-3">
    <Icon className="w-4 h-4 text-primary" />
    <h4 className="font-bold uppercase tracking-tight text-sm">{label}</h4>
    <Badge variant="outline" className="text-[10px]">{count}</Badge>
  </div>
);

/**
 * Aggregated client profile dialog. Pulls user, linked marketing business,
 * tickets, quotes, invoices, subscriptions, bookings, and contact log via
 * a single call to /api/admin/clients/{user_id}/profile.
 */
const ClientProfileDialog = ({ userId, open, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !userId) return;
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/admin/clients/${userId}/profile`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to load profile'))
      .finally(() => setLoading(false));
  }, [userId, open]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="bg-card border-border max-w-4xl max-h-[88vh] overflow-y-auto"
        data-testid="client-profile-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-bold uppercase flex items-center gap-2">
            <UserIcon className="w-5 h-5 text-primary" />
            {data?.user?.name || 'Client Profile'}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="py-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
          </div>
        )}

        {!loading && data && (
          <div className="space-y-5">
            <ContactCard user={data.user} business={data.business} summary={data.summary} />

            <QuickActionToolbar
              user={data.user}
              business={data.business}
              onAfterAction={() => {
                // Re-fetch the profile so newly-created tickets/quotes/etc surface immediately.
                setLoading(true);
                axios.get(`${BACKEND_URL}/api/admin/clients/${userId}/profile`, { withCredentials: true })
                  .then((r) => setData(r.data))
                  .finally(() => setLoading(false));
              }}
            />

            <Tabs defaultValue="tickets" className="w-full">
              <TabsList className="grid grid-cols-6 w-full">
                <TabsTrigger value="tickets" data-testid="profile-tab-tickets">
                  Tickets ({data.summary.tickets_total})
                </TabsTrigger>
                <TabsTrigger value="quotes" data-testid="profile-tab-quotes">
                  Quotes ({data.summary.quotes_total})
                </TabsTrigger>
                <TabsTrigger value="invoices" data-testid="profile-tab-invoices">
                  Invoices ({data.summary.invoices_total})
                </TabsTrigger>
                <TabsTrigger value="subs" data-testid="profile-tab-subs">
                  Subs ({data.summary.subscriptions_total})
                </TabsTrigger>
                <TabsTrigger value="bookings" data-testid="profile-tab-bookings">
                  Bookings ({data.summary.bookings_total})
                </TabsTrigger>
                <TabsTrigger value="comms" data-testid="profile-tab-comms">
                  Comms
                </TabsTrigger>
              </TabsList>

              <TabsContent value="tickets" className="mt-4">
                <SectionHeader icon={TicketIcon} label="Tickets" count={data.tickets.length} />
                {data.tickets.length === 0 ? <Empty msg="No tickets yet." /> : (
                  <div className="space-y-2">
                    {data.tickets.map((t) => (
                      <Card key={t.ticket_id} className="p-3 border-border" data-testid={`profile-ticket-${t.ticket_id}`}>
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-sm">{t.title}</span>
                              <Badge variant="outline" className={`text-[10px] ${STATUS_BADGE[t.status] || ''}`}>
                                {t.status}
                              </Badge>
                              <Badge variant="outline" className="text-[10px] uppercase">{t.priority}</Badge>
                              {t.category && <Badge variant="outline" className="text-[10px]">{t.category}</Badge>}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{t.description}</p>
                          </div>
                          <span className="text-[11px] text-muted-foreground shrink-0">{fmt(t.created_at)}</span>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="quotes" className="mt-4">
                <SectionHeader icon={FileText} label="Quotes" count={data.quotes.length} />
                {data.quotes.length === 0 ? <Empty msg="No quotes yet." /> : (
                  <div className="space-y-2">
                    {data.quotes.map((q) => (
                      <Card key={q.quote_id} className="p-3 border-border" data-testid={`profile-quote-${q.quote_id}`}>
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-sm">{q.title || 'Untitled quote'}</span>
                              <Badge variant="outline" className={`text-[10px] ${STATUS_BADGE[q.status] || ''}`}>
                                {q.status}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              ${(q.total_amount || 0).toFixed(2)} · {(q.items || []).length} item{(q.items || []).length === 1 ? '' : 's'}
                            </p>
                          </div>
                          <span className="text-[11px] text-muted-foreground shrink-0">{fmt(q.created_at)}</span>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="invoices" className="mt-4">
                <SectionHeader icon={Receipt} label="Invoices" count={data.invoices.length} />
                {data.invoices.length === 0 ? <Empty msg="No invoices yet." /> : (
                  <div className="space-y-2">
                    {data.invoices.map((inv) => (
                      <Card key={inv.invoice_id} className="p-3 border-border" data-testid={`profile-invoice-${inv.invoice_id}`}>
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-sm font-mono">
                                {inv.wave_invoice_number ? `#${inv.wave_invoice_number}` : inv.invoice_id}
                              </span>
                              <Badge variant="outline" className={`text-[10px] ${STATUS_BADGE[(inv.status || '').toLowerCase()] || ''}`}>
                                {inv.status}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">{inv.description || '—'}</p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="font-bold text-sm">${(inv.amount || 0).toFixed(2)}</p>
                            <p className="text-[11px] text-muted-foreground">{fmt(inv.created_at)}</p>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="subs" className="mt-4">
                <SectionHeader icon={Briefcase} label="Subscriptions" count={data.subscriptions.length} />
                {data.subscriptions.length === 0 ? <Empty msg="No subscriptions." /> : (
                  <div className="space-y-2">
                    {data.subscriptions.map((s) => (
                      <Card key={s.subscription_id} className="p-3 border-border" data-testid={`profile-sub-${s.subscription_id}`}>
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div>
                            <p className="font-bold text-sm">{s.service_name}</p>
                            <p className="text-[11px] text-muted-foreground">
                              ${(s.monthly_price || 0).toFixed(2)} · {s.billing_cycle}
                            </p>
                          </div>
                          <Badge variant="outline" className={`text-[10px] ${STATUS_BADGE[s.status] || ''}`}>
                            {s.status}
                          </Badge>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="bookings" className="mt-4">
                <SectionHeader icon={Calendar} label="Bookings" count={data.bookings.length} />
                {data.bookings.length === 0 ? <Empty msg="No bookings on file." /> : (
                  <div className="space-y-2">
                    {data.bookings.map((b) => (
                      <Card key={b.booking_id} className="p-3 border-border" data-testid={`profile-booking-${b.booking_id}`}>
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div>
                            <p className="font-bold text-sm">{fmt(b.start_at, 'EEE MMM dd · h:mm a')}</p>
                            <p className="text-[11px] text-muted-foreground">{b.topic || '—'}</p>
                          </div>
                          <Badge variant="outline" className={`text-[10px] ${STATUS_BADGE[b.status] || ''}`}>
                            {b.status}
                          </Badge>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="comms" className="mt-4">
                <SectionHeader icon={MessageCircle} label="Communication log" count={(data.business?.contact_log || []).length} />
                {!data.business || (data.business.contact_log || []).length === 0 ? (
                  <Empty msg="No marketing/contact history." />
                ) : (
                  <div className="space-y-2">
                    {[...(data.business.contact_log || [])].reverse().map((c, i) => (
                      <Card key={i} className="p-3 border-border" data-testid={`profile-comm-${i}`}>
                        <div className="flex items-start gap-3">
                          <Badge variant="outline" className="text-[10px] uppercase shrink-0">
                            {c.channel || 'manual'}
                          </Badge>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs">{c.note || '—'}</p>
                            <p className="text-[10px] text-muted-foreground mt-1">
                              {fmt(c.at, 'MMM dd yyyy · h:mm a')} {c.actor && `· ${c.actor}`}
                            </p>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ContactCard = ({ user, business, summary }) => (
  <Card className="p-4 border-primary/30 bg-primary/5" data-testid="profile-contact-card">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-1.5">
        <div className="flex items-center gap-2 text-sm">
          <Mail className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="font-mono">{user.email}</span>
        </div>
        {business?.phone_number && (
          <div className="flex items-center gap-2 text-sm">
            <Phone className="w-3.5 h-3.5 text-muted-foreground" />
            <span>{business.phone_number}</span>
          </div>
        )}
        {business?.formatted_address && (
          <div className="flex items-start gap-2 text-sm">
            <MapPin className="w-3.5 h-3.5 text-muted-foreground mt-0.5" />
            <span className="text-xs">{business.formatted_address}</span>
          </div>
        )}
        {business?.website && (
          <div className="flex items-center gap-2 text-sm">
            <Globe className="w-3.5 h-3.5 text-muted-foreground" />
            <a href={business.website} target="_blank" rel="noopener noreferrer" className="text-primary text-xs hover:underline">
              {business.website.replace(/^https?:\/\//, '').split('/')[0]}
            </a>
          </div>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <Stat label="Open Tickets" value={summary.tickets_open} />
        <Stat label="Open Quotes" value={summary.quotes_open} />
        <Stat label="Unpaid Inv." value={summary.invoices_unpaid} />
        <Stat label="Active Subs" value={summary.subscriptions_active} />
        <Stat label="Bookings" value={summary.bookings_total} />
        <Stat label="Role" value={user.role} small />
      </div>
    </div>
  </Card>
);

const Stat = ({ label, value, small }) => (
  <div className="rounded-md border border-border p-2">
    <p className={`font-bold ${small ? 'text-xs uppercase' : 'text-lg'}`}>{value}</p>
    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
  </div>
);

const Empty = ({ msg }) => (
  <p className="text-sm text-muted-foreground italic py-4 text-center">{msg}</p>
);

// ---------------------------------------------------------------------------
// Quick Action toolbar — Create Ticket / Send Quote / Open Booking / Send Email
// ---------------------------------------------------------------------------

const QuickActionToolbar = ({ user, business, onAfterAction }) => {
  const [active, setActive] = useState(null); // 'ticket' | 'quote' | 'booking' | 'email' | null

  return (
    <Card className="p-3 border-border bg-background/40" data-testid="profile-quick-actions">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground mr-1">Quick actions:</span>
        <ActionButton icon={Plus} label="Create Ticket" testId="qa-ticket" active={active === 'ticket'} onClick={() => setActive(active === 'ticket' ? null : 'ticket')} />
        <ActionButton icon={FileEdit} label="Send Quote" testId="qa-quote" active={active === 'quote'} onClick={() => setActive(active === 'quote' ? null : 'quote')} />
        <ActionButton icon={CalendarPlus} label="Open Booking" testId="qa-booking" active={active === 'booking'} onClick={() => setActive(active === 'booking' ? null : 'booking')} />
        <ActionButton icon={Send} label="Send Email" testId="qa-email" active={active === 'email'} onClick={() => setActive(active === 'email' ? null : 'email')} />
        <ActionButton icon={KeyIcon} label="Add Credential" testId="qa-credential" active={active === 'credential'} onClick={() => setActive(active === 'credential' ? null : 'credential')} />
      </div>
      {active === 'ticket' && <CreateTicketForm user={user} onClose={() => setActive(null)} onDone={onAfterAction} />}
      {active === 'quote' && <CreateQuoteForm user={user} business={business} onClose={() => setActive(null)} onDone={onAfterAction} />}
      {active === 'booking' && <CreateBookingForm user={user} onClose={() => setActive(null)} onDone={onAfterAction} />}
      {active === 'email' && <SendEmailForm user={user} onClose={() => setActive(null)} onDone={onAfterAction} />}
      {active === 'credential' && <AddCredentialForm user={user} onClose={() => setActive(null)} onDone={onAfterAction} />}
    </Card>
  );
};

const ActionButton = ({ icon: Icon, label, testId, active, onClick }) => (
  <Button
    size="sm"
    variant={active ? 'default' : 'outline'}
    onClick={onClick}
    data-testid={testId}
    className={active ? 'font-bold' : ''}
  >
    <Icon className="w-3.5 h-3.5 mr-1.5" />
    {label}
  </Button>
);

const FormShell = ({ title, onClose, children, submitting, onSubmit, submitLabel = 'Submit', submitTestId }) => (
  <div className="mt-3 pt-3 border-t border-border space-y-3">
    <h5 className="text-xs uppercase tracking-widest text-muted-foreground">{title}</h5>
    {children}
    <div className="flex justify-end gap-2 pt-1">
      <Button variant="ghost" size="sm" onClick={onClose} disabled={submitting}>Cancel</Button>
      <Button size="sm" onClick={onSubmit} disabled={submitting} className="font-bold uppercase" data-testid={submitTestId}>
        {submitting ? 'Saving…' : submitLabel}
      </Button>
    </div>
  </div>
);

const CreateTicketForm = ({ user, onClose, onDone }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [category, setCategory] = useState('general');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!title.trim() || !description.trim()) return toast.error('Title and description required');
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/tickets`, {
        user_id: user.user_id,
        title: title.trim(), description: description.trim(), priority, category,
      }, { withCredentials: true });
      toast.success(`Ticket created for ${user.name}`);
      onClose();
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormShell title="New Ticket" onClose={onClose} submitting={submitting} onSubmit={submit} submitLabel="Create Ticket" submitTestId="qa-ticket-submit">
      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" className="bg-input" data-testid="qa-ticket-title" />
      <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe the issue" className="bg-input" data-testid="qa-ticket-desc" />
      <div className="grid grid-cols-2 gap-2">
        <Select value={priority} onValueChange={setPriority}>
          <SelectTrigger className="bg-input" data-testid="qa-ticket-priority"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="high">High</SelectItem><SelectItem value="urgent">Urgent</SelectItem>
          </SelectContent>
        </Select>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="bg-input" data-testid="qa-ticket-category"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="general">General</SelectItem><SelectItem value="technical">Technical</SelectItem>
            <SelectItem value="billing">Billing</SelectItem><SelectItem value="network">Network</SelectItem>
            <SelectItem value="security">Security</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </FormShell>
  );
};

const CreateQuoteForm = ({ user, business, onClose, onDone }) => {
  const [title, setTitle] = useState('');
  const [itemDesc, setItemDesc] = useState('');
  const [qty, setQty] = useState(1);
  const [unitPrice, setUnitPrice] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!itemDesc.trim() || !unitPrice) return toast.error('Item description and unit price required');
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/quotes`, {
        user_id: user.user_id,
        client_name: user.name,
        client_email: user.email,
        client_address: business?.formatted_address || '',
        items: [{ description: itemDesc.trim(), quantity: parseFloat(qty) || 1, unit_price: parseFloat(unitPrice) }],
        tax_rate: 0,
        valid_days: 30,
        notes: title.trim() || '',
      }, { withCredentials: true });
      toast.success(`Draft quote created — finish it from the Quotes tab`);
      onClose();
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create quote');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormShell title="Draft Quote" onClose={onClose} submitting={submitting} onSubmit={submit} submitLabel="Save Draft" submitTestId="qa-quote-submit">
      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Internal note (optional)" className="bg-input" data-testid="qa-quote-title" />
      <Input value={itemDesc} onChange={(e) => setItemDesc(e.target.value)} placeholder="Line item description" className="bg-input" data-testid="qa-quote-item" />
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[11px]">Quantity</Label>
          <Input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} className="bg-input" data-testid="qa-quote-qty" />
        </div>
        <div>
          <Label className="text-[11px]">Unit price ($)</Label>
          <Input type="number" step="0.01" value={unitPrice} onChange={(e) => setUnitPrice(e.target.value)} className="bg-input" data-testid="qa-quote-price" />
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground">Saved as a draft. Add more line items, taxes, and send from the Quotes tab.</p>
    </FormShell>
  );
};

const CreateBookingForm = ({ user, onClose, onDone }) => {
  // 30-min slots over the next 14 weekdays, M-F 9am-5pm. Local time → UTC ISO.
  const slotChoices = React.useMemo(() => {
    const out = [];
    const now = new Date();
    for (let d = 0; d < 14 && out.length < 30; d++) {
      const date = new Date(now);
      date.setDate(now.getDate() + d);
      const wd = date.getDay();
      if (wd === 0 || wd === 6) continue;
      for (let h = 9; h < 17; h++) {
        for (const m of [0, 30]) {
          const slot = new Date(date);
          slot.setHours(h, m, 0, 0);
          if (slot.getTime() < Date.now() + 60 * 60 * 1000) continue;
          out.push(slot.toISOString());
        }
      }
    }
    return out.slice(0, 40);
  }, []);

  const [start, setStart] = useState(slotChoices[0] || '');
  const [duration, setDuration] = useState(30);
  const [topic, setTopic] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!start) return toast.error('Pick a time slot');
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/bookings`, {
        name: user.name, email: user.email,
        start_at: start, duration_minutes: parseInt(duration, 10),
        topic: topic.trim(), status: 'confirmed',
      }, { withCredentials: true });
      toast.success(`Booking confirmed for ${user.name}`);
      onClose();
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create booking');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormShell title="New Booking" onClose={onClose} submitting={submitting} onSubmit={submit} submitLabel="Confirm Booking" submitTestId="qa-booking-submit">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[11px]">Slot</Label>
          <Select value={start} onValueChange={setStart}>
            <SelectTrigger className="bg-input" data-testid="qa-booking-slot"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-[260px]">
              {slotChoices.map((iso) => (
                <SelectItem key={iso} value={iso}>{format(new Date(iso), 'EEE MMM dd · h:mm a')}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px]">Duration (min)</Label>
          <Select value={String(duration)} onValueChange={(v) => setDuration(parseInt(v, 10))}>
            <SelectTrigger className="bg-input" data-testid="qa-booking-duration"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="15">15</SelectItem><SelectItem value="30">30</SelectItem>
              <SelectItem value="45">45</SelectItem><SelectItem value="60">60</SelectItem>
              <SelectItem value="90">90</SelectItem><SelectItem value="120">120</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <Textarea rows={2} value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Topic / agenda (optional)" className="bg-input" data-testid="qa-booking-topic" />
    </FormShell>
  );
};

const SendEmailForm = ({ user, onClose, onDone }) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!subject.trim() || !message.trim()) return toast.error('Subject and message required');
    setSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/clients/${user.user_id}/send-email`, {
        subject: subject.trim(), message,
      }, { withCredentials: true });
      toast.success(`Email sent to ${res.data.to_email}`);
      onClose();
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Email failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormShell title={`Email ${user.email}`} onClose={onClose} submitting={submitting} onSubmit={submit} submitLabel="Send" submitTestId="qa-email-submit">
      <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" className="bg-input" data-testid="qa-email-subject" />
      <Textarea rows={5} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Hi there,&#10;&#10;..." className="bg-input" data-testid="qa-email-body" />
    </FormShell>
  );
};

const AddCredentialForm = ({ user, onClose, onDone }) => {
  const [systemName, setSystemName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [url, setUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!systemName.trim() || !username.trim() || !password) {
      return toast.error('System, username, and password required');
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/credentials`, {
        user_id: user.user_id,
        system_name: systemName.trim(),
        username: username.trim(),
        password, url: url.trim() || null, notes: notes.trim() || null,
      }, { withCredentials: true });
      toast.success(`Credential saved for ${user.name}`);
      onClose();
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add credential');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormShell title="New Credential" onClose={onClose} submitting={submitting} onSubmit={submit} submitLabel="Save Credential" submitTestId="qa-credential-submit">
      <Input value={systemName} onChange={(e) => setSystemName(e.target.value)} placeholder="System / app name (e.g. Office 365 admin)" className="bg-input" data-testid="qa-cred-system" />
      <div className="grid grid-cols-2 gap-2">
        <Input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Username" className="bg-input" data-testid="qa-cred-username" />
        <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password (encrypted at rest)" className="bg-input" data-testid="qa-cred-password" />
      </div>
      <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="URL (optional)" className="bg-input" data-testid="qa-cred-url" />
      <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Internal notes (optional)" className="bg-input" data-testid="qa-cred-notes" />
      <p className="text-[11px] text-muted-foreground">Stored encrypted via Fernet. The client and admins can reveal it later from the Credentials tab.</p>
    </FormShell>
  );
};

export default ClientProfileDialog;
